import { useState, useEffect, useCallback, useRef } from "react";
import { TOUR_TIMING } from "./constants";

// Lerp helper for smooth rect interpolation
const lerpRect = (from, to, t) => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  width: from.width + (to.width - from.width) * t,
  height: from.height + (to.height - from.height) * t,
});

// Ease-out cubic for smooth deceleration
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const ANIMATION_DURATION_MS = 400;

const SpotlightOverlay = ({ step, onInteract }) => {
  const [displayRect, setDisplayRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const retryRef = useRef(null);
  const prevRectRef = useRef(null);
  const animFrameRef = useRef(null);
  const targetRectRef = useRef(null);

  const measureTarget = useCallback(() => {
    if (!step?.target) {
      targetRectRef.current = null;
      return false;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      targetRectRef.current = null;
      return false;
    }
    const rect = el.getBoundingClientRect();
    const padding = 10;
    targetRectRef.current = {
      x: rect.x - padding,
      y: rect.y - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
    return true;
  }, [step]);

  // Animate from previous rect to new rect
  const animateToRect = useCallback((newRect) => {
    cancelAnimationFrame(animFrameRef.current);
    const fromRect = prevRectRef.current || newRect;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
      const eased = easeOutCubic(progress);
      const interpolated = lerpRect(fromRect, newRect, eased);
      setDisplayRect(interpolated);

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        prevRectRef.current = newRect;
      }
    };

    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  // Find the nearest scrollable ancestor of an element
  const findScrollableParent = (el) => {
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  };

  // Scroll a container so the element is centered within it
  const scrollContainerToEl = (container, el) => {
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const elCenterInContainer = (elRect.top - containerRect.top) + container.scrollTop + (elRect.height / 2);
    const targetScroll = elCenterInContainer - (container.clientHeight / 2);
    container.scrollTo({ top: Math.max(0, targetScroll), behavior: "smooth" });
  };

  // Handle scrolling based on step properties
  const scrollToTarget = useCallback((el) => {
    if (!el || !step) return Promise.resolve();

    // noScroll - skip scrolling entirely
    if (step.noScroll) return Promise.resolve();

    // smooth-to-top - scroll to top first, then measure
    if (step.scrollBehavior === "smooth-to-top") {
      return new Promise((resolve) => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(resolve, 800);
      });
    }

    // Check if target is inside a scrollable container (e.g. sticky left panel)
    const scrollableParent = findScrollableParent(el);
    if (scrollableParent) {
      scrollContainerToEl(scrollableParent, el);
    }

    // smooth - gentle scroll into view
    if (step.scrollBehavior === "smooth") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      return new Promise((resolve) => setTimeout(resolve, 800));
    }

    // Default - scroll into view with center alignment
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    return new Promise((resolve) => setTimeout(resolve, TOUR_TIMING.SCROLL_SETTLE_MS));
  }, [step]);

  // Wait for target element to appear in DOM
  useEffect(() => {
    setVisible(false);
    if (!step) return;

    const waitSelector = step.waitFor || step.target;
    if (!waitSelector) {
      setVisible(true);
      return;
    }

    let elapsed = 0;
    const interval = 200;

    const check = () => {
      const el = document.querySelector(waitSelector);
      if (el) {
        scrollToTarget(el).then(() => {
          if (measureTarget()) {
            animateToRect(targetRectRef.current);
          } else {
            setDisplayRect(null);
          }
          setVisible(true);
        });
        return;
      }
      elapsed += interval;
      if (elapsed < TOUR_TIMING.ELEMENT_WAIT_TIMEOUT_MS) {
        retryRef.current = setTimeout(check, interval);
      } else {
        // Element not found, show without spotlight
        setDisplayRect(null);
        setVisible(true);
      }
    };

    check();
    return () => {
      clearTimeout(retryRef.current);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [step, measureTarget, scrollToTarget, animateToRect]);

  // Reposition on resize/scroll (live update without animation)
  useEffect(() => {
    if (!visible) return;
    const handler = () => {
      if (measureTarget()) {
        // Direct update during scroll/resize - no animation
        const rect = targetRectRef.current;
        setDisplayRect(rect);
        prevRectRef.current = rect;
      }
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [visible, measureTarget]);

  // Clean up animation frame on unmount
  useEffect(() => {
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  if (!step || !visible) return null;

  // Tooltip positioning - prefer below the target
  let tooltipStyle = {};
  if (displayRect) {
    const tooltipW = 380;
    const tooltipH = 160;
    let top = displayRect.y + displayRect.height + 20;
    let left = displayRect.x;

    if (step.placement === "right") {
      left = displayRect.x + displayRect.width + 20;
      top = displayRect.y;
    } else if (step.placement === "left") {
      left = displayRect.x - tooltipW - 20;
      top = displayRect.y;
    } else if (step.placement === "top") {
      top = displayRect.y - tooltipH - 20;
    }

    // Keep on screen
    if (left + tooltipW > window.innerWidth - 16) left = window.innerWidth - tooltipW - 16;
    if (left < 16) left = 16;
    if (top + tooltipH > window.innerHeight - 80) top = displayRect.y - tooltipH - 20;
    if (top < 16) top = 16;

    tooltipStyle = { top, left, maxWidth: tooltipW };
  } else {
    // No target - center the tooltip
    tooltipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: step.centered ? 520 : 440,
    };
  }

  return (
    <div className="et-spotlight" onClick={(e) => e.stopPropagation()}>
      {/* SVG mask overlay */}
      <svg className="et-spotlight__mask" width="100%" height="100%">
        <defs>
          <mask id="et-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {displayRect && (
              <rect
                x={displayRect.x}
                y={displayRect.y}
                width={displayRect.width}
                height={displayRect.height}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(6, 13, 26, 0.7)"
          mask="url(#et-spotlight-mask)"
        />
      </svg>

      {/* Glow ring */}
      {displayRect && (
        <div
          className="et-spotlight__glow"
          style={{
            top: displayRect.y - 2,
            left: displayRect.x - 2,
            width: displayRect.width + 4,
            height: displayRect.height + 4,
          }}
        />
      )}

      {/* Tooltip */}
      <div className={`et-spotlight__tooltip${step.centered ? " et-spotlight__tooltip--centered" : ""}`} style={tooltipStyle}>
        <h3 className="et-spotlight__title">{step.title}</h3>
        {step.allowHtml ? (
          <div className="et-spotlight__desc" dangerouslySetInnerHTML={{ __html: step.description }} />
        ) : (
          <p className="et-spotlight__desc">{step.description}</p>
        )}
      </div>
    </div>
  );
};

export default SpotlightOverlay;
